import React, {useState} from 'react';

import {Button, CheckIcon, InputNumber, ModalPopup} from '@kit';

import {wordByCount} from '../../../../../../components/utils';
import {useCharacter} from '../../../character-context';
import {useFeatureAction} from '../use-feature-action';
import type {ProcedureModalProps} from './procedure-modal-props';

import css from '../../sheet.module.css';

/** выбор потраченных ячеек, которые вернёт Магическое или Природное восстановление */
export const SlotRecoveryModal: React.FC<ProcedureModalProps<'slotRecovery'>> = ({action, procedure, onClose}) => {
	const {character} = useCharacter();
	const {spend, canUse} = useFeatureAction(action);
	const [counts, setCounts] = useState<Record<string, number>>({});
	const {levels: budget, maxSlotLevel} = procedure;

	const slots = character.spellSlots.filter((slot) => !slot.pact && slot.level <= maxSlotLevel && slot.spent > 0);
	const chosen = slots.reduce((total, slot) => total + slot.level * (counts[slot.key] || 0), 0);

	const setCount = (key: string, count: number) => setCounts((current) => ({...current, [key]: count}));

	const recover = () => {
		const recovered = slots.filter((slot) => counts[slot.key]);
		spend(recovered.map((slot) => `${slot.level} круг × ${counts[slot.key]}`).join(', '), (current) => ({
			spentSpellSlots: {
				...current.spentSpellSlots,
				...Object.fromEntries(
					recovered.map((slot) => [
						slot.key,
						Math.max(0, (current.spentSpellSlots[slot.key] || 0) - counts[slot.key]),
					]),
				),
			},
		}));
		onClose();
	};

	return (
		<ModalPopup open onClose={onClose} header={action.title} maxWidth={480}>
			<p className={css.muted}>
				После Короткого отдыха можно вернуть потраченные ячейки суммарно до{' '}
				{wordByCount(budget, ['круга', 'кругов', 'кругов'], true)}, ни одной выше {maxSlotLevel}-го круга.
			</p>
			{slots.length ? (
				<div className={css.formRows}>
					{slots.map((slot) => {
						const count = counts[slot.key] || 0;
						const affordable = Math.floor((budget - chosen + slot.level * count) / slot.level);
						return (
							<div key={slot.key} className={css.formRow}>
								<span>
									{slot.level} круг
									<span className={css.muted}> · потрачено {slot.spent}</span>
								</span>
								<InputNumber
									min={0}
									max={Math.min(slot.spent, affordable)}
									value={count}
									onChange={(value) =>
										setCount(slot.key, Math.max(0, Math.min(slot.spent, affordable, value || 0)))
									}
								/>
							</div>
						);
					})}
				</div>
			) : (
				<p className={css.muted}>Нет потраченных ячеек, которые можно вернуть.</p>
			)}
			<p>
				Выбрано кругов: <b>{chosen}</b> из {budget}
			</p>
			<div className={css.modalButtons}>
				<Button appearance="secondary" onClick={onClose}>
					Отмена
				</Button>
				<Button appearance="primary" icon={<CheckIcon />} disabled={!canUse || !chosen} onClick={recover}>
					Восстановить
				</Button>
			</div>
		</ModalPopup>
	);
};
